import { AdminRole, Permission } from '../types';

// Three real staff tiers (auth-service): super_admin (full, manages admins),
// admin (business + operations, no admin management), operator (technical/fleet
// only — no billing, customer PII, revenue, or admin management).
export const ROLE_PERMISSIONS: Record<AdminRole, Permission[]> = {
  super_admin: [
    'users:create', 'users:read', 'users:update', 'users:delete',
    'devices:read', 'devices:update', 'devices:control', 'devices:delete',
    'fleet:diagnose', 'fleet:remote_action',
    'sim:read', 'sim:topup',
    'billing:read', 'billing:comp', 'billing:refund', 'billing:edit_plan',
    'alerts:read', 'alerts:manage',
    'tickets:read', 'tickets:manage',
    'broadcast:send',
    'analytics:read',
    'audit:read',
    'admins:manage',
  ],
  admin: [
    'users:create', 'users:read', 'users:update', 'users:delete',
    'devices:read', 'devices:update', 'devices:control', 'devices:delete',
    'fleet:diagnose', 'fleet:remote_action',
    'sim:read', 'sim:topup',
    'billing:read', 'billing:comp', 'billing:refund', 'billing:edit_plan',
    'alerts:read', 'alerts:manage',
    'tickets:read', 'tickets:manage',
    'broadcast:send',
    'analytics:read',
    'audit:read',
    // No admins:manage (reserved for super_admin)
  ],
  operator: [
    // Technical / fleet only.
    'devices:read', 'devices:update', 'devices:control', 'devices:delete',
    'fleet:diagnose', 'fleet:remote_action',
    'sim:read', 'sim:topup',
    'tickets:read', 'tickets:manage',
    // No billing, no customer PII, no revenue/analytics, no admin management.
  ],
};

export function hasPermission(role: AdminRole, permission: Permission): boolean {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(permission);
}

// Which sidebar modules each role may open. Operator is the technical tier:
// fleet, cameras, cloud storage, SIM, tickets — but not billing, customers,
// revenue, broadcasts, or admin management.
export function canAccessModule(role: AdminRole, moduleId: string): boolean {
  switch (moduleId) {
    case 'overview':
    case 'security':
      return true;

    // Technical / fleet — super_admin, admin, operator
    case 'customer-fleets':
    case 'cameras':
    case 'camera-map':
    case 'diagnostics':
    case 'camera-versions':
    case 'offline-triage':
    case 'cloud-storage':
    case 'sim-management':
    case 'tickets':
      return ['super_admin', 'admin', 'operator'].includes(role);

    // Business / billing / customers — admin + super_admin only
    case 'customers':
    case 'customer-360':
    case 'subscriptions':
    case 'payments':
    case 'plans':
    case 'inventory':
    case 'orders':
    case 'alerts':
    case 'emergencies':
    case 'audit-log':
    case 'broadcast':
    case 'analytics':
    case 'ai-usage':
      return ['super_admin', 'admin'].includes(role);

    // Managing other admins — super_admin only
    case 'admins':
      return role === 'super_admin';

    default:
      return false;
  }
}
